import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TabPane,
  Spinner,
} from "reactstrap"
import classNames from "classnames"
import { useTranslation } from "react-i18next"
import { getProduct, updateProduct, deleteProduct } from "../../../store/products/actions"
import { AddNewProduct } from "./Modals"

const ProductDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === "ar"

  // Get data from Redux
  const { selectedProduct: product, loading, error, updateSuccess } = useSelector(
    (state: any) => state.Products
  )

  const [activeTab, setActiveTab] = useState("0")
  const [copied, setCopied] = useState(false)
  const [showAddNewProduct, setShowAddNewProduct] = useState(false)

  // Fetch product on mount
  useEffect(() => {
    if (id) {
      dispatch(getProduct(Number(id), { push: navigate }))
    }
  }, [dispatch, id, navigate])

  // Refetch product after successful update
  useEffect(() => {
    if (updateSuccess && id) {
      dispatch(getProduct(Number(id), { push: navigate }))
    }
  }, [updateSuccess, id, dispatch, navigate])

  // Toggle active status
  const toggleActiveStatus = useCallback(() => {
    if (product) {
      dispatch(updateProduct(
        product.id,
        {
          enName: product.enName,
          arName: product.arName,
          price: product.price,
          categoryId: product.categoryId,
          isActive: !product.isActive,
          availableQuantity: product.totalAvailableQuantity,
        },
        { push: navigate }
      ))
    }
  }, [dispatch, navigate, product])

  // Delete product
  const deleteProductHandler = useCallback(() => {
    dispatch(deleteProduct(product.id, { push: navigate }))
    navigate("/products")
  }, [dispatch, navigate, product, t])

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
  }

  const imageShow = (img: string, id: number) => {
    const expandImg = document.getElementById("expandedImg" + id)
    if (expandImg) {
      ;(expandImg as HTMLImageElement).src = img
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  // Loading state
  if (loading && !product) {
    return (
      <div className="page-content">
        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner color="primary" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <h5 className="text-danger">{t("products_errorLoadingProduct") || "Error loading product"}</h5>
            <Button color="primary" onClick={() => navigate("/products")}>
              {t("common_back") || "Back"}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Product not found
  if (!product) {
    return (
      <div className="page-content">
        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <h5>{t("products_productNotFound") || "Product not found"}</h5>
            <Button color="primary" onClick={() => navigate("/products")}>
              {t("common_back") || "Back"}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Use real backend images if available, otherwise use placeholder
  const subImage = product.images && product.images.length > 0
    ? product.images.map((img: any, index: number) => ({
        id: img.id.toString(),
        cover: img.isPrimary || img.isMain,
        imgSrc: img.filePath
          ? `${process.env.REACT_APP_BACKEND_IMAGE_URL}${img.filePath}`
          : "https://placehold.co/300x300/png?text=No+Image",
      }))
    : [{
        id: "1",
        cover: true,
        imgSrc: "https://placehold.co/300x300/png?text=No+Image",
      }]

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("products_products")}
          subBreadcrumbItem={isArabic ? product.arName : product.enName}
        />
        <div className="d-flex gap-2">
          <Button
            onClick={() => setShowAddNewProduct(true)}
            className="bg-gradiant-blue border-0 text-capitalize"
          >
            {t("products_editProduct")}
          </Button>
          <Button
            onClick={deleteProductHandler}
            className="bg-transparent border text-danger border-danger text-capitalize"
          >
            {t("products_deleteProduct")}
          </Button>
        </div>
      </div>
      <Card className="shadow-md rounded-3">
        <CardBody>
          <Row>
            <Col xl={4}>
              <div className="product-detai-imgs">
                <TabContent activeTab={activeTab}>
                  {subImage.length &&
                    subImage.map((item: any, index: number) => (
                      <TabPane tabId={index.toString()} key={item.id}>
                        <div>
                          <img
                            src={item.imgSrc}
                            alt=""
                            id="expandedImg1"
                            className="img-fluid mx-auto d-block"
                            style={{ maxWidth: "300px" }}
                          />
                        </div>
                      </TabPane>
                    ))}
                </TabContent>
                <Nav
                  className="flex bg-transparent bg-none justify-content-center"
                  pills
                >
                  {subImage.length &&
                    subImage.map((item: any, index: number) => (
                      <NavItem key={item.id}>
                        <NavLink
                          className={classNames(
                            {
                              active: activeTab === index.toString(),
                            },
                            "p-1 position-relative overflow-hidden"
                          )}
                          onClick={() => {
                            toggleTab(index.toString())
                          }}
                          style={{ width: "62px", height: "62px" }}
                        >
                          <img
                            src={item.imgSrc}
                            alt=""
                            onClick={() => {
                              imageShow(item.imgSrc, index + 1)
                            }}
                            className="img-fluid mx-auto d-block rounded w-100"
                          />
                          <button
                            className="position-absolute p-0 border-0 translate-middle text-danger fw-bold bg-white rounded-circle text-center"
                            style={{
                              top: "15px",
                              left: "45px",
                              width: "16px",
                              height: "16px",
                            }}
                          >
                            <i className="bx bx-x bx-xs fw-bolder "></i>
                          </button>
                          {item.cover && (
                            <p
                              className="position-absolute m-auto w-90 text-semi-dark fw-bold bg-semi-white-60 rounded-2 text-center font-size-10 px-2"
                              style={{ bottom: "10px", left: "8px" }}
                            >
                              {t("common_cover")}
                            </p>
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                </Nav>
              </div>
            </Col>

            <Col xl="8">
              <div className="mt-4 mt-xl-3">
                <div className="d-flex justify-content-between">
                  <h4 className="text-semi-dark fw-semibold m-0">
                    {isArabic ? product.arName : product.enName}
                  </h4>
                  <p className="text-semi-dark fw-semibold font-size-16">
                    {t("common_qar")} {product.price.toFixed(2)}
                  </p>
                </div>
                <p>
                  <span className="fw-regular font-size-16 ">
                    {product.categoryName || "-"}
                  </span>
                  <span className="font-size-12 px-3">
                    {product.brand && (
                      <>
                        {t("products_brand") || "Brand"}: {product.brand}
                      </>
                    )}
                  </span>
                </p>

                {/* Description */}
                {product.description && (
                  <div className="mb-3">
                    <span className="font-size-14 fw-regular text-muted">
                      {product.description}
                    </span>
                  </div>
                )}

                <div className="d-flex gap-2 mb-4">
                  {product.productType && (
                    <Badge className="bg-primary text-white font-size-12 p-2">
                      {product.productType}
                    </Badge>
                  )}
                  {product.isPinned && (
                    <Badge className="bg-success text-white font-size-12 p-2">
                      {t("products_pinned") || "Pinned"}
                    </Badge>
                  )}
                </div>

                <div className="d-flex justify-content-between mb-4">
                  <p className="text-muted mb-0">
                    <span className="font-size-16 fw-semibold text-semi-dark">
                      {t("products_productId")}
                    </span>
                    <span className="font-size-16 fw-regular text-black px-3">
                      {product.id}
                    </span>
                  </p>
                  <button
                    className="position-relative cursor-pointer border-0 bg-transparent"
                    onClick={() => {
                      copyToClipboard(product.id.toString())
                    }}
                  >
                    <i className="bx bx-copy px-2 text-primary bx-sm cursor-pointer"></i>
                    {copied && (
                      <span
                        className="position-absolute  translate-middle-x mt-1 text-success small"
                        style={{ top: "0", left: "-7px" }}
                      >
                        {t("common_copied")}
                      </span>
                    )}
                  </button>
                </div>

                {/* Supplier Info */}
                {product.supplierName && (
                  <div className="mb-4 p-3 bg-light rounded-3">
                    <div className="d-flex align-items-center gap-3">
                      {product.supplierLogo && (
                        <img
                          src={product.supplierLogo}
                          alt={product.supplierName}
                          className="rounded-circle"
                          style={{ width: "40px", height: "40px", objectFit: "cover" }}
                        />
                      )}
                      <div>
                        <div className="font-size-14 fw-semibold text-semi-dark">
                          {product.supplierName}
                        </div>
                        <div className="font-size-12 text-muted">
                          {product.supplierRating && (
                            <>
                              <i className="bx bx-star text-warning"></i>
                              {product.supplierRating}
                            </>
                          )}
                          {product.supplierIsVerified && (
                            <span className="badge bg-success text-white ms-2 font-size-10">
                              <i className="bx bx-check-circle"></i>
                              {t("products_verified") || "Verified"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <span className="font-size-16 fw-semibold text-semi-dark">
                    {t("products_salesCount") || "Sales Count"}
                  </span>
                  <span className="font-size-16 fw-regular text-black px-3">
                    __
                  </span>
                </div>

                <div className="d-flex justify-content-between mb-4">
                  <p className="text-muted mb-0">
                    <span className="font-size-16 fw-semibold text-semi-dark">
                      {t("products_availableStock")}
                    </span>
                    <span className="font-size-16 fw-regular text-black px-3">
                      {product.totalAvailableQuantity}
                    </span>
                  </p>
                  <p className="text-muted mb-0">
                    <span className="font-size-16 fw-semibold text-semi-dark">
                      {t("products_lowStock") || "Low Stock"}
                    </span>
                    <span className="font-size-16 fw-regular text-black px-3">
                      __ {t("products_default") || "Units"}
                    </span>
                  </p>
                </div>

                <Row>
                  <Col lg={6}>
                    <div className="table-responsive shadow-md">
                      <Table className="table mb-0 table-bordered rounded-3 overflow-hidden ">
                        <thead>
                          <tr>
                            <th
                              style={{ width: "400px" }}
                              className={"text-capitalize p-2"}
                            >
                              {t("products_composition")}
                            </th>
                            <th className="p-2">{t("products_value")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td
                              style={{ width: "400px" }}
                              className={"text-capitalize"}
                            >
                              PH
                            </td>
                            <td>7.1</td>
                          </tr>
                          <tr>
                            <td
                              style={{ width: "400px" }}
                              className={"text-capitalize"}
                            >
                              Sodium
                            </td>
                            <td>7.5</td>
                          </tr>
                          <tr>
                            <td
                              style={{ width: "400px" }}
                              className={"text-capitalize"}
                            >
                              Potassium
                            </td>
                            <td>20</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="table-responsive shadow-md">
                      <Table className="table mb-0 table-bordered rounded-3 overflow-hidden ">
                        <thead>
                          <tr>
                            <th
                              style={{ width: "400px" }}
                              className={"text-capitalize p-2"}
                            >
                              {t("products_composition")}
                            </th>
                            <th className="p-2">{t("products_value")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td
                              style={{ width: "400px" }}
                              className={"text-capitalize"}
                            >
                              Calcium
                            </td>
                            <td>36</td>
                          </tr>
                          <tr>
                            <td
                              style={{ width: "400px" }}
                              className={"text-capitalize"}
                            >
                              Sulphate
                            </td>
                            <td>24</td>
                          </tr>
                          <tr>
                            <td
                              style={{ width: "400px" }}
                              className={"text-capitalize"}
                            >
                              Tds
                            </td>
                            <td>175</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex gap-2 align-items-center mt-4">
                  <p className="text-semi-dark text-start">
                    <b>{t("products_productVisibility")}</b>
                    <span> ({t("products_productVisibilityInfo2")})</span>
                  </p>
                  <div className="form-check form-switch mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`customSwitch${product.id}`}
                      checked={product.isActive}
                      onClick={(e) => {
                        e.preventDefault()
                        toggleActiveStatus()
                      }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Edit Product Modal */}
      <AddNewProduct
        isOpen={showAddNewProduct}
        toggle={() => setShowAddNewProduct(false)}
        product={product}
      />
    </div>
  )
}

export default ProductDetails
